/**
 * Conversation Service
 * Manages conversation context for follow-up queries and analytics
 */

import { enrichWithContractData } from './contractService';

class ConversationService {
  constructor() {
    this.conversationHistory = [];
    this.lastQuery = null;
    this.lastResults = null;
  }

  /**
   * Add a message to conversation history
   */
  addMessage(role, content) {
    this.conversationHistory.push({
      role,
      content,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Store last query and results for follow-ups
   */
  storeQuery(query, results) {
    this.lastQuery = query;
    this.lastResults = results;
  }

  /**
   * Get conversation context for LLM
   */
  getContext() {
    return {
      history: this.conversationHistory.slice(-5), // Last 5 messages
      lastQuery: this.lastQuery,
      lastResults: this.lastResults ? {
        count: this.lastResults.length,
        layer: this.lastQuery?.layer,
        summary: this.getSummary(this.lastResults)
      } : null
    };
  }

  /**
   * Generate summary of results (synchronous - for backward compatibility)
   * Updated for real TPL data field names
   */
  getSummary(results) {
    if (!results || results.length === 0) return null;

    const summary = {
      totalCount: results.length,
      byField: {}
    };

    // Group by common fields
    const firstResult = results[0];
    const props = firstResult.attributes || firstResult.properties || {};

    // Detect if this is tracts or wells data
    const isWells = props.API || props.WellName || props.ENVOperator;
    const isTracts = props.TractID || props.CountyName || props.TractAcres;

    // Count by operator (TPL uses ENVOperator for wells, Operator for tracts)
    const operatorField = props.ENVOperator ? 'ENVOperator' :
                          props.Operator ? 'Operator' :
                          props.OPERATOR ? 'OPERATOR' :
                          props.operator ? 'operator' : null;
    if (operatorField) {
      summary.byField.operator = this.groupBy(results, r => {
        const p = r.attributes || r.properties || {};
        return p.ENVOperator || p.Operator || p.OPERATOR || p.operator || 'Unknown';
      });
    }

    // Count by county (TPL uses CountyName or County)
    const countyField = props.CountyName ? 'CountyName' :
                        props.County ? 'County' :
                        props.COUNTY ? 'COUNTY' :
                        props.county ? 'county' : null;
    if (countyField) {
      summary.byField.county = this.groupBy(results, r => {
        const p = r.attributes || r.properties || {};
        return p.CountyName || p.County || p.COUNTY || p.county || 'Unknown';
      });
    }

    // Count by well status (TPL uses ENVWellStatus)
    if (props.ENVWellStatus || props.WellStatus || props.LEASE_STATUS) {
      summary.byField.leaseStatus = this.groupBy(results, r => {
        const p = r.attributes || r.properties || {};
        return p.ENVWellStatus || p.WellStatus || p.LEASE_STATUS || 'Unknown';
      });
    }

    // Count by formation (TPL uses ENVInterval)
    if (props.ENVInterval || props.Formation) {
      summary.byField.formation = this.groupBy(results, r => {
        const p = r.attributes || r.properties || {};
        return p.ENVInterval || p.Formation || 'Unknown';
      });
    }

    // Sum acreage fields (TPL uses TractAcres, GrossAcres)
    const acreageField = props.TractAcres ? 'TractAcres' :
                         props.GrossAcres ? 'GrossAcres' :
                         props.ACREAGE ? 'ACREAGE' :
                         props.acreage ? 'acreage' : null;
    if (acreageField) {
      summary.totalAcreage = results.reduce((sum, r) => {
        const p = r.attributes || r.properties || {};
        return sum + (parseFloat(p.TractAcres || p.GrossAcres || p.ACREAGE || p.acreage) || 0);
      }, 0);
    }

    // Sum NMA (Net Mineral Acres) - TPL terminology
    if (props.NMA || props.NRA || props.ROYALTY_ACRES) {
      summary.totalRoyaltyAcres = results.reduce((sum, r) => {
        const p = r.attributes || r.properties || {};
        return sum + (parseFloat(p.NMA || p.NRA || p.ROYALTY_ACRES) || 0);
      }, 0);
    }

    // Sum NRI (Net Revenue Interest) for wells
    if (props.NRI || props.ActiveNRI || props.AllocatedNRI) {
      summary.totalNRI = results.reduce((sum, r) => {
        const p = r.attributes || r.properties || {};
        return sum + (parseFloat(p.NRI || p.ActiveNRI || p.AllocatedNRI) || 0);
      }, 0);
    }

    // Sum ORRI (Override Royalty Interest)
    if (props.ORRI) {
      summary.totalORRI = results.reduce((sum, r) => {
        const p = r.attributes || r.properties || {};
        return sum + (parseFloat(p.ORRI) || 0);
      }, 0);
    }

    // Track data type
    summary.dataType = isWells ? 'wells' : isTracts ? 'tracts' : 'unknown';

    return summary;
  }

  /**
   * Generate enriched summary with contract data (async)
   */
  async getEnrichedSummary(results) {
    const summary = this.getSummary(results);
    if (!summary) return null;

    // Fetch and enrich with contract data
    try {
      const { contractStats } = await enrichWithContractData(results);
      if (contractStats) {
        summary.contracts = contractStats;
      }
    } catch (error) {
      console.error('Failed to enrich with contract data:', error);
    }

    return summary;
  }

  /**
   * Group results by a field
   */
  groupBy(results, keyFn) {
    const groups = {};
    results.forEach(r => {
      const key = keyFn(r);
      groups[key] = (groups[key] || 0) + 1;
    });
    return groups;
  }

  /**
   * Check if query is a follow-up
   */
  isFollowUp(query) {
    const followUpIndicators = [
      'show me just',
      'only the',
      'only',
      'just',
      'filter',
      'what about',
      'how many',
      'what is',
      'what\'s the',
      'summarize',
      'average',
      'total',
      'count',
      'those',
      'these',
      'them',
      'with',
      'where',
      'that have'
    ];

    const lowerQuery = query.toLowerCase();
    return followUpIndicators.some(indicator => lowerQuery.includes(indicator));
  }

  /**
   * Check if query is a filter request (refine existing results)
   */
  isFilterQuery(query) {
    const lowerQuery = query.toLowerCase();

    // Anything that looks like a NEW search/query is NOT a filter, regardless of other keywords.
    // Proximity queries ("within 100ft"), grade/risk queries, and explicit "show/list/which/build"
    // imperatives all run fresh — they're not refinements of the prior result.
    const isNewSearch = /\b(show|list|find|build|create|which|what|where|near|within\s+\d+|by\s+(district|operator|grade)|all\s+(grade|critical))\b/i.test(lowerQuery);
    if (isNewSearch) return false;

    // Word-boundary filter indicators — must be standalone words, not substrings.
    const filterRegex = /\b(only|just|filter|refine|narrow down|where|that have|greater than|less than|more than)\b/i;
    if (filterRegex.test(lowerQuery)) {
      return true;
    }

    // Check for status refinements without geographic changes
    // If query mentions lease status (active/pending/expired) but NOT a new location/county
    const hasStatusKeyword = /\b(active|pending|expired|terminated)\b/i.test(lowerQuery);
    const hasNewLocation = /\b(county|in [A-Z]|tracts? in|show.*in)\b/i.test(query); // Check for new geographic filter

    if (hasStatusKeyword && !hasNewLocation) {
      // This is likely a status filter on existing results
      return true;
    }

    return false;
  }

  /**
   * Filter existing results based on natural language criteria
   * Updated for real TPL data field names
   */
  filterResults(results, query) {
    if (!results || results.length === 0) return [];

    const lowerQuery = query.toLowerCase();
    let filtered = [...results];

    console.log('🔍 filterResults called:', { query, lowerQuery, inputCount: results.length });

    // ============================================
    // COUNTY FILTER (works for both tracts & wells)
    // ============================================
    let countyMatch = lowerQuery.match(/(?:only|just|in|filter to|show)\s+(\w+)\s+county/i);
    if (!countyMatch) {
      const onlyMatch = lowerQuery.match(/(?:only|just)\s+(\w+)/i);
      if (onlyMatch) {
        const word = onlyMatch[1].toLowerCase();
        const excludedKeywords = [
          'active', 'pending', 'expired', 'terminated', 'inactive', 'producing', 'duc', 'drilling',
          'diamondback', 'eog', 'chevron', 'devon', 'pioneer', 'bp', 'shell', 'apache', 'mewbourne',
          'coterra', 'conocophillips', 'conoco', 'occidental', 'oxy', 'ovintiv', 'exxon', 'mobil',
          'marathon', 'cimarex', 'centennial', 'parsley', 'callon', 'sm', 'energen', 'jagged',
          'fasken', 'henry', 'encana', 'anadarko', 'noble', 'laredo', 'matador', 'ring', 'centennial',
          'highpeak', 'permian', 'endeavor', 'summit', 'tap', 'grenadier', 'birch',
          'the', 'leases', 'tracts', 'wells', 'wolfcamp', 'bone', 'spring', 'assets', 'holdings',
          'lease', 'easement', 'surface', 'mineral', 'row', 'perpetual'
        ];
        if (!excludedKeywords.includes(word)) {
          countyMatch = onlyMatch;
        }
      }
    }
    if (countyMatch) {
      const county = countyMatch[1];
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const featureCounty = p.CountyName || p.County || p.COUNTY || p.county || '';
        return featureCounty.toLowerCase().includes(county.toLowerCase());
      });
      console.log(`🏛️ County filter (${county}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // OPERATOR FILTER (wells only - ENVOperator)
    // ============================================
    const knownOperators = [
      'diamondback', 'eog', 'mewbourne', 'pioneer', 'chevron', 'exxon', 'bp', 'shell', 'apache',
      'devon', 'occidental', 'ovintiv', 'conoco', 'conocophillips', 'coterra', 'marathon',
      'cimarex', 'centennial', 'parsley', 'callon', 'sm energy', 'energen', 'jagged peak',
      'fasken', 'henry', 'encana', 'anadarko', 'noble', 'laredo', 'matador', 'ring energy',
      'highpeak', 'endeavor', 'summit', 'tap rock', 'grenadier', 'birch'
    ];
    let operatorMatch = lowerQuery.match(/(?:operator|operated by)\s+(\w+(?:\s+\w+)?)/i);
    if (!operatorMatch) {
      operatorMatch = lowerQuery.match(/the\s+(\w+(?:\s+\w+)?)\s+(?:ones|tracts|wells|leases)/i);
    }
    if (!operatorMatch) {
      for (const op of knownOperators) {
        if (lowerQuery.includes(op)) {
          operatorMatch = [null, op];
          break;
        }
      }
    }
    if (operatorMatch && !lowerQuery.includes('by operator')) {
      const op = operatorMatch[1];
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const featureOperator = p.ENVOperator || p.Operator || p.OPERATOR || p.operator || '';
        return featureOperator.toLowerCase().includes(op.toLowerCase());
      });
      console.log(`🏢 Operator filter (${op}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // WELL STATUS FILTER (wells - ENVWellStatus)
    // ============================================
    const wellStatuses = [];
    if (/\bproducing\b/i.test(lowerQuery)) wellStatuses.push('PRODUCING');
    if (/\bduc\b|drilled uncompleted/i.test(lowerQuery)) wellStatuses.push('DUC');
    if (/\bdrilling\b/i.test(lowerQuery)) wellStatuses.push('DRILLING');
    if (/\bpermitted\b/i.test(lowerQuery)) wellStatuses.push('PERMITTED');
    if (/shut.?in/i.test(lowerQuery)) wellStatuses.push('SHUT-IN');
    if (/p&a|plugged/i.test(lowerQuery)) wellStatuses.push('P&A');

    if (wellStatuses.length > 0) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const featureStatus = (p.ENVWellStatus || p.WellStatus || '').toUpperCase();
        return wellStatuses.some(status => featureStatus.includes(status));
      });
      console.log(`📊 Well status filter (${wellStatuses.join(',')}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // CONTRACT/TRACT STATUS FILTER (tracts - Status field)
    // ============================================
    const contractStatuses = [];
    if (/\bactive\b/i.test(lowerQuery) && !lowerQuery.includes('nri')) contractStatuses.push('ACTIVE');
    if (/\bpending\b/i.test(lowerQuery)) contractStatuses.push('PENDING');
    if (/\bexpired\b/i.test(lowerQuery)) contractStatuses.push('EXPIRED');
    if (/\bterminated\b/i.test(lowerQuery)) contractStatuses.push('TERMINATED');
    if (/\binactive\b/i.test(lowerQuery)) contractStatuses.push('INACTIVE');
    if (/\bsuspended\b/i.test(lowerQuery)) contractStatuses.push('SUSPENDED');

    if (contractStatuses.length > 0) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const status = (p.Status || p.ContractStatus || p.LEASE_STATUS || '').toUpperCase();
        return contractStatuses.some(s => status.includes(s));
      });
      console.log(`📋 Contract status filter (${contractStatuses.join(',')}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // CONTRACT TYPE FILTER (tracts - ContractType)
    // ============================================
    const contractTypes = [];
    if (/\blease\b/i.test(lowerQuery) && !lowerQuery.includes('sublease')) contractTypes.push('LEASE');
    if (/\beasement\b/i.test(lowerQuery)) contractTypes.push('EASEMENT');
    if (/\bsurface\b/i.test(lowerQuery)) contractTypes.push('SURFACE');
    if (/\bmineral\b/i.test(lowerQuery)) contractTypes.push('MINERAL');
    if (/\brow\b|right.?of.?way/i.test(lowerQuery)) contractTypes.push('ROW');
    if (/\bsublease\b/i.test(lowerQuery)) contractTypes.push('SUBLEASE');
    if (/\broyalty\b/i.test(lowerQuery) && !lowerQuery.includes('acres')) contractTypes.push('ROYALTY');

    if (contractTypes.length > 0) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const cType = (p.ContractType || p.Type || '').toUpperCase();
        return contractTypes.some(t => cType.includes(t));
      });
      console.log(`📝 Contract type filter (${contractTypes.join(',')}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // TRACT TYPE FILTER (tracts - TractType: N, S, etc.)
    // ============================================
    const tractTypeMatch = lowerQuery.match(/tract\s*type\s*[=:]?\s*(\w)/i);
    if (tractTypeMatch) {
      const tType = tractTypeMatch[1].toUpperCase();
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        return (p.TractType || '').toUpperCase() === tType;
      });
      console.log(`🏷️ Tract type filter (${tType}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // BLOCK FILTER (tracts - Block)
    // ============================================
    const blockMatch = lowerQuery.match(/block\s+([0-9]+\s*[a-z0-9]*)/i);
    if (blockMatch) {
      const block = blockMatch[1].trim();
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const featureBlock = (p.Block || '').toString().toLowerCase();
        return featureBlock.includes(block.toLowerCase());
      });
      console.log(`🧱 Block filter (${block}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // SECTION FILTER (tracts - Sec)
    // ============================================
    const sectionMatch = lowerQuery.match(/section\s+(\d+)/i);
    if (sectionMatch) {
      const section = sectionMatch[1];
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        return (p.Sec || '').toString() === section;
      });
      console.log(`📐 Section filter (${section}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // DEAL NAME FILTER (both tracts & wells - DealName)
    // ============================================
    const dealMatch = lowerQuery.match(/deal\s+(?:name\s+)?["']?([^"']+)["']?/i);
    if (dealMatch) {
      const deal = dealMatch[1].trim();
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const dealName = (p.DealName || '').toLowerCase();
        return dealName.includes(deal.toLowerCase());
      });
      console.log(`🤝 Deal name filter (${deal}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // FORMATION/INTERVAL FILTER (wells - ENVInterval)
    // ============================================
    const formations = [];
    if (/wolfcamp/i.test(lowerQuery)) formations.push('WOLFCAMP');
    if (/bone\s*spring/i.test(lowerQuery)) formations.push('BONE SPRING');
    if (/spraberry/i.test(lowerQuery)) formations.push('SPRABERRY');
    if (/delaware/i.test(lowerQuery)) formations.push('DELAWARE');
    if (/midland/i.test(lowerQuery) && !lowerQuery.includes('county')) formations.push('MIDLAND');
    if (/avalon/i.test(lowerQuery)) formations.push('AVALON');

    if (formations.length > 0) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const interval = (p.ENVInterval || p.Formation || '').toUpperCase();
        return formations.some(fm => interval.includes(fm));
      });
      console.log(`⛏️ Formation filter (${formations.join(',')}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // ACREAGE FILTER (tracts - TractAcres)
    // ============================================
    const acreageMatch = lowerQuery.match(/acreage?\s*(>|<|=|greater than|less than|more than|over|under)\s*(\d+)/i);
    if (acreageMatch) {
      const op = acreageMatch[1].toLowerCase();
      const value = parseFloat(acreageMatch[2]);
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const acreage = parseFloat(p.TractAcres || p.GrossAcres || p.UnitAcres || p.acreage || 0);
        if (op === '>' || op.includes('greater') || op.includes('more') || op.includes('over')) {
          return acreage > value;
        } else if (op === '<' || op.includes('less') || op.includes('under')) {
          return acreage < value;
        } else {
          return acreage === value;
        }
      });
      console.log(`📏 Acreage filter (${op} ${value}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // NRI FILTER (both - NRI, ActiveNRI)
    // ============================================
    const nriMatch = lowerQuery.match(/nri\s*(>|<|=|greater than|less than|over)\s*(\d+(?:\.\d+)?)/i);
    if (nriMatch) {
      const op = nriMatch[1].toLowerCase();
      const value = parseFloat(nriMatch[2]);
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const nri = parseFloat(p.NRI || p.ActiveNRI || p.AllocatedNRI || 0);
        if (op === '>' || op.includes('greater') || op.includes('over')) {
          return nri > value;
        } else if (op === '<' || op.includes('less')) {
          return nri < value;
        } else {
          return nri === value;
        }
      });
      console.log(`💰 NRI filter (${op} ${value}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // PERPETUAL FILTER (tracts - Perpetual)
    // ============================================
    if (/\bperpetual\b/i.test(lowerQuery)) {
      const beforeCount = filtered.length;
      const wantPerpetual = !lowerQuery.includes('not perpetual') && !lowerQuery.includes('non-perpetual');
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        const isPerpetual = p.Perpetual === 'Y' || p.Perpetual === true || p.Perpetual === 1;
        return wantPerpetual ? isPerpetual : !isPerpetual;
      });
      console.log(`♾️ Perpetual filter (${wantPerpetual}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // GRANTEE FILTER (tracts - Grantee)
    // ============================================
    const granteeMatch = lowerQuery.match(/grantee\s+["']?([^"']+)["']?/i);
    if (granteeMatch) {
      const grantee = granteeMatch[1].trim();
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        return (p.Grantee || '').toLowerCase().includes(grantee.toLowerCase());
      });
      console.log(`👤 Grantee filter (${grantee}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // GRANTOR FILTER (tracts - Grantor)
    // ============================================
    const grantorMatch = lowerQuery.match(/grantor\s+["']?([^"']+)["']?/i);
    if (grantorMatch) {
      const grantor = grantorMatch[1].trim();
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        return (p.Grantor || '').toLowerCase().includes(grantor.toLowerCase());
      });
      console.log(`👥 Grantor filter (${grantor}): ${beforeCount} → ${filtered.length}`);
    }

    // ============================================
    // VINTAGE/YEAR FILTER (wells - Vintage)
    // ============================================
    const vintageMatch = lowerQuery.match(/(?:vintage|year|from)\s*(\d{4})/i);
    if (vintageMatch) {
      const year = vintageMatch[1];
      const beforeCount = filtered.length;
      filtered = filtered.filter(f => {
        const p = f.attributes || f.properties || {};
        return (p.Vintage || '').toString().includes(year);
      });
      console.log(`📅 Vintage filter (${year}): ${beforeCount} → ${filtered.length}`);
    }

    console.log(`🎯 Filter complete: ${results.length} → ${filtered.length}`);
    return filtered;
  }

  /**
   * Detect if query is asking for analytics
   */
  isAnalyticsQuery(query) {
    const analyticsKeywords = [
      'how many',
      'what is the average',
      'what\'s the total',
      'summarize',
      'count',
      'sum',
      'average',
      'breakdown',
      'distribution',
      'compare'
    ];

    const lowerQuery = query.toLowerCase();
    return analyticsKeywords.some(keyword => lowerQuery.includes(keyword));
  }

  /**
   * Generate analytics response
   */
  generateAnalytics(query, results) {
    if (!results || results.length === 0) {
      return "No data available for analytics.";
    }

    const summary = this.getSummary(results);
    const lowerQuery = query.toLowerCase();

    let response = [];

    // Count queries
    if (lowerQuery.includes('how many')) {
      response.push(`Total: **${summary.totalCount}** records`);
    }

    // Average acreage
    if (lowerQuery.includes('average') && summary.totalAcreage) {
      const avg = (summary.totalAcreage / summary.totalCount).toFixed(2);
      response.push(`Average acreage: **${avg}** acres`);
    }

    // Total acreage
    if (lowerQuery.includes('total') && summary.totalAcreage) {
      response.push(`Total surface acreage: **${summary.totalAcreage.toLocaleString()}** acres`);
    }

    // Total royalty acres
    if (summary.totalRoyaltyAcres) {
      response.push(`Total net royalty acres: **${summary.totalRoyaltyAcres.toLocaleString()}** acres`);
    }

    // Breakdown by operator
    if ((lowerQuery.includes('operator') || lowerQuery.includes('breakdown'))
        && summary.byField.operator) {
      response.push(`\n**By Operator:**`);
      Object.entries(summary.byField.operator)
        .sort((a, b) => b[1] - a[1])
        .forEach(([operator, count]) => {
          response.push(`- ${operator}: ${count} tracts`);
        });
    }

    // Breakdown by county
    if ((lowerQuery.includes('county') || lowerQuery.includes('breakdown'))
        && summary.byField.county) {
      response.push(`\n**By County:**`);
      Object.entries(summary.byField.county)
        .sort((a, b) => b[1] - a[1])
        .forEach(([county, count]) => {
          response.push(`- ${county}: ${count} tracts`);
        });
    }

    // Breakdown by lease status
    if (summary.byField.leaseStatus) {
      response.push(`\n**By Lease Status:**`);
      Object.entries(summary.byField.leaseStatus)
        .sort((a, b) => b[1] - a[1])
        .forEach(([status, count]) => {
          response.push(`- ${status}: ${count} tracts`);
        });
    }

    return response.length > 0 ? response.join('\n') : "Analysis complete.";
  }

  /**
   * Clear conversation history
   */
  clear() {
    this.conversationHistory = [];
    this.lastQuery = null;
    this.lastResults = null;
  }

  /**
   * Smart Analyst - Generate contextual insights and follow-up suggestions
   * This is the WOW feature that makes the assistant proactive
   */
  generateSmartInsights(results, query) {
    if (!results || results.length === 0) return null;

    const summary = this.getSummary(results);
    const insights = [];
    const suggestions = [];

    // Extract current context from query to avoid redundant suggestions
    const queryLower = (query || '').toLowerCase();
    const currentCountyMatch = queryLower.match(/\b(borden|crane|culberson|dawson|ector|glasscock|howard|lea|loving|martin|midland|pecos|reagan|reeves|upton|ward|winkler)\b/i);
    const currentCounty = currentCountyMatch ? currentCountyMatch[1].toUpperCase() : null;
    const currentOperatorMatch = queryLower.match(/\b(diamondback|eog|occidental|mewbourne|devon|conocophillips|exxon|coterra|apache|pioneer)\b/i);
    const currentOperator = currentOperatorMatch ? currentOperatorMatch[1] : null;

    // Analyze what's interesting about this data
    const operators = summary.byField?.operator || {};
    const counties = summary.byField?.county || {};
    const statuses = summary.byField?.leaseStatus || {};
    const formations = summary.byField?.formation || {};

    const operatorCount = Object.keys(operators).length;
    const countyCount = Object.keys(counties).length;
    const statusCount = Object.keys(statuses).length;

    // Find top operator
    let topOperator = null;
    let topOperatorCount = 0;
    Object.entries(operators).forEach(([op, count]) => {
      if (count > topOperatorCount) {
        topOperator = op;
        topOperatorCount = count;
      }
    });

    // Find top county
    let topCounty = null;
    let topCountyCount = 0;
    Object.entries(counties).forEach(([county, count]) => {
      if (count > topCountyCount) {
        topCounty = county;
        topCountyCount = count;
      }
    });

    // Check for high-value items (ORRI > 2%)
    let highOrriCount = 0;
    let highNriCount = 0;
    let producingCount = 0;
    let ducCount = 0;

    results.forEach(f => {
      const p = f.attributes || f.properties || {};
      if (parseFloat(p.ORRI) > 2) highOrriCount++;
      if (parseFloat(p.NRI || p.ActiveNRI) > 0.5) highNriCount++;
      if ((p.ENVWellStatus || '').toUpperCase() === 'PRODUCING') producingCount++;
      if ((p.ENVWellStatus || '').toUpperCase() === 'DUC') ducCount++;
    });

    // Generate insight text
    const insightParts = [];

    if (operatorCount > 1 && topOperator) {
      insightParts.push(`**${operatorCount} operators** active (${topOperator} leads with ${topOperatorCount})`);
    }

    if (highOrriCount > 0) {
      insightParts.push(`**${highOrriCount}** tracts have ORRI above 2% (high-value)`);
    }

    if (highNriCount > 0) {
      insightParts.push(`**${highNriCount}** have NRI above 0.5%`);
    }

    if (producingCount > 0 && ducCount > 0) {
      insightParts.push(`**${producingCount}** producing, **${ducCount}** DUC (completion opportunities)`);
    } else if (producingCount > 0) {
      insightParts.push(`**${producingCount}** currently producing`);
    }

    if (statusCount > 1) {
      const statusList = Object.entries(statuses)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([s, c]) => `${s}: ${c}`)
        .join(', ');
      insightParts.push(`Status breakdown: ${statusList}`);
    }

    // Generate follow-up suggestions based on data
    if (highOrriCount > 0 && highOrriCount < results.length) {
      suggestions.push({
        text: `Focus on high-ORRI tracts (${highOrriCount} with >2%)`,
        query: `Show only tracts with ORRI over 2%`
      });
    }

    // Only suggest operator drill-down if not already filtered to an operator
    if (operatorCount > 1 && topOperator && !currentOperator) {
      suggestions.push({
        text: `Explore ${topOperator}'s holdings (${topOperatorCount} assets)`,
        query: `Show only ${topOperator} assets`
      });
    }

    if (ducCount > 0) {
      suggestions.push({
        text: `See DUC completion opportunities (${ducCount} wells)`,
        query: `Show only DUC wells`
      });
    }

    // Only suggest county drill-down if not already filtered to that county
    if (countyCount > 1 && topCounty && (!currentCounty || topCounty.toUpperCase() !== currentCounty)) {
      suggestions.push({
        text: `Drill into ${topCounty} County (${topCountyCount} assets)`,
        query: `Show only ${topCounty} County`
      });
    }

    // Always offer analytics
    if (results.length > 5) {
      suggestions.push({
        text: `Run full analytics breakdown`,
        query: `Summarize these results by operator and county`
      });
    }

    // Limit to 3 most relevant suggestions
    const topSuggestions = suggestions.slice(0, 3);

    // Only return if we have something interesting to say
    if (insightParts.length === 0 && topSuggestions.length === 0) {
      return null;
    }

    return {
      insights: insightParts,
      suggestions: topSuggestions,
      summary: {
        totalCount: summary.totalCount,
        operatorCount,
        countyCount,
        highOrriCount,
        producingCount,
        ducCount
      }
    };
  }
  /**
   * Detect if query is a symbology/color change request
   */
  isSymbologyChangeQuery(query) {
    const colorKeywords = [
      'color', 'colour', 'make', 'change', 'set',
      'highlight', 'show as', 'display as'
    ];
    const targetKeywords = [
      'producing', 'duc', 'drilling', 'permitted', 'wells',
      'tracts', 'royalty', 'fee', 'orri', 'surface',
      'status', 'type'
    ];
    const colorNames = [
      'red', 'blue', 'green', 'yellow', 'orange', 'purple',
      'pink', 'cyan', 'gray', 'grey', 'black', 'white',
      'violet', 'indigo', 'teal', 'lime', 'amber'
    ];

    const lowerQuery = query.toLowerCase();

    const hasColorKeyword = colorKeywords.some(k => lowerQuery.includes(k));
    const hasTargetKeyword = targetKeywords.some(k => lowerQuery.includes(k));
    const hasColorName = colorNames.some(c => lowerQuery.includes(c));

    return (hasColorKeyword || hasColorName) && (hasTargetKeyword || hasColorName);
  }

  /**
   * Parse symbology change request
   * Returns: { target: 'tractType' | 'wellStatus', value: string, color: string }
   */
  parseSymbologyChange(query) {
    const lowerQuery = query.toLowerCase();

    // Color name to hex mapping
    const colorMap = {
      'red': '#dc2626',
      'blue': '#2563eb',
      'green': '#16a34a',
      'yellow': '#eab308',
      'orange': '#f97316',
      'purple': '#7c3aed',
      'violet': '#8b5cf6',
      'pink': '#ec4899',
      'cyan': '#06b6d4',
      'teal': '#14b8a6',
      'gray': '#6b7280',
      'grey': '#6b7280',
      'lime': '#84cc16',
      'amber': '#f59e0b',
      'indigo': '#6366f1'
    };

    // Extract color
    let color = null;
    for (const [name, hex] of Object.entries(colorMap)) {
      if (lowerQuery.includes(name)) {
        color = hex;
        break;
      }
    }

    if (!color) return null;

    // Well status patterns
    const wellStatusPatterns = [
      { pattern: /producing/i, value: 'PRODUCING' },
      { pattern: /duc|drilled uncompleted/i, value: 'DUC' },
      { pattern: /drilling/i, value: 'DRILLING' },
      { pattern: /permitted/i, value: 'PERMITTED' },
      { pattern: /completed/i, value: 'COMPLETED' },
      { pattern: /inactive/i, value: 'INACTIVE PRODUCER' },
      { pattern: /shut.?in/i, value: 'SHUT-IN' },
      { pattern: /p\s*&\s*a|plugged/i, value: 'P & A' }
    ];

    for (const { pattern, value } of wellStatusPatterns) {
      if (pattern.test(lowerQuery)) {
        return { target: 'wellStatus', value, color };
      }
    }

    // Tract type patterns
    const tractTypePatterns = [
      { pattern: /\bfee\b|ownership/i, value: 'F' },
      { pattern: /\borri\b|overrid|royalty interest/i, value: 'O' },
      { pattern: /non.?participat/i, value: 'N' },
      { pattern: /undivided/i, value: 'U' },
      { pattern: /qualified/i, value: 'Q' },
      { pattern: /vendor/i, value: 'V' }
    ];

    for (const { pattern, value } of tractTypePatterns) {
      if (pattern.test(lowerQuery)) {
        return { target: 'tractType', value, color };
      }
    }

    // Generic "all wells" or "all tracts"
    if (/all\s+wells|wells?\s+(to|as)/i.test(lowerQuery)) {
      return { target: 'allWells', value: '*', color };
    }
    if (/all\s+tracts|tracts?\s+(to|as)/i.test(lowerQuery)) {
      return { target: 'allTracts', value: '*', color };
    }

    return null;
  }
}

// Singleton instance
const conversationService = new ConversationService();

export default conversationService;
